// Roadblock Service - Single Responsibility: Manage roadblocks only
import { apiFetch, ROADBLOCKS_V1_ENABLED } from '../config/api'

// ── Types ──────────────────────────────────────────────────────────────────

export type RoadblockStage =
  | 'identify' | 'understand' | 'stabilize' | 'experiment'
  | 'practice' | 'strengthen' | 'integrate'

export interface Roadblock {
  id: number | string
  title: string
  description: string
  status: string
  progress: number
  priority: 'low' | 'medium' | 'high' | 'urgent'
  severity: 'low' | 'medium' | 'high' | 'critical'
  impact_level?: 'low' | 'medium' | 'high' | 'critical'
  roadblock_type?: string
  category?: string
  resolution_notes?: string
  created_at: string
  updated_at?: string
  parent_type?: 'dream' | 'aspiration' | 'goal' | 'task' | 'action_item' | null
  parent_id?: number | null
  parent_title?: string | null
  blocking_item_type?: string
  severity_score?: number | null
  readiness_score?: number | null
  confidence_to_face_score?: number | null
  perceived_control_score?: number | null
  stage?: RoadblockStage
  stage_updated_at?: string | null
  stage_summary_text?: string | null
  checkins?: Array<Record<string, unknown>>
  interventions?: Intervention[]
  practice_reps?: PracticeRep[]
  evidence?: EvidenceItem[]
  suggestions?: Suggestion[]
  progress_snapshots?: Array<Record<string, unknown>>
}

export interface Intervention {
  id: number
  roadblock_id: number
  title: string
  description?: string
  intervention_type: string
  source: string
  status: string
  scheduled_at?: string
  started_at?: string
  ended_at?: string
  effectiveness_score?: number
  before_feeling?: string
  after_feeling?: string
  note_text?: string
  created_at: string
  updated_at: string
}

export interface PracticeRep {
  id: number
  roadblock_id: number
  title: string
  description?: string
  rep_type: string
  status: string
  difficulty_score?: number
  before_fear_score?: number
  after_fear_score?: number
  before_confidence_score?: number
  after_confidence_score?: number
  started_at?: string
  ended_at?: string
  outcome_summary?: string
  note_text?: string
  created_at: string
  updated_at: string
}

export interface EvidenceItem {
  id: number
  roadblock_id: number
  evidence_type: string
  source: string
  title: string
  content: string
  confidence_level?: number
  is_pinned: boolean
  created_at: string
  updated_at: string
}

export interface Suggestion {
  id: number
  roadblock_id: number
  suggestion_type: string
  title: string
  description?: string
  source: string
  status: string
  payload: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface StageInfo {
  stage: RoadblockStage
  stage_label: string
  stage_updated_at?: string
  stage_summary_text?: string
  stage_source?: string
}

export interface StageRecommendation {
  current_stage: RoadblockStage
  current_label: string
  recommended_stage: RoadblockStage
  recommended_label: string
  reasons: string[]
  is_change: boolean
  compatible_statuses: string[]
}

export interface StageTransition {
  id: number
  roadblock_id: number
  from_stage?: string
  to_stage: string
  transition_reason_text?: string
  transition_source?: string
  accepted_by_user?: boolean
  created_at: string
}

export interface Followup {
  id: number
  roadblock_id: number
  followup_type: string
  stage_snapshot: string
  status_snapshot: string
  prompt_text: string
  quick_actions_json: Array<{value: string; label: string}>
  outcome_status: string
  trigger_reason: string
  trigger_reason_display: string
  referenced_evidence_item_id?: number
  referenced_intervention_id?: number
  referenced_practice_rep_id?: number
  sent_at?: string
  responded_at?: string
  dismissed_at?: string
  snoozed_until?: string
  created_at: string
}

export interface CreateRoadblockRequest {
  title: string
  description: string
  priority: 'low' | 'medium' | 'high'
  related_item_id?: string
  severity: 'low' | 'medium' | 'high'
}

export interface UpdateRoadblockRequest {
  title?: string
  description?: string
  status?: string
  progress?: number
  priority?: 'low' | 'medium' | 'high'
  related_item_id?: string
  severity?: 'low' | 'medium' | 'high'
  resolution_notes?: string
  parent_goal_id?: number
}

// ── Stage labels for UI ────────────────────────────────────────────────────

export const STAGE_LABELS: Record<RoadblockStage, string> = {
  identify: 'Identifying',
  understand: 'Understanding',
  stabilize: 'Stabilizing',
  experiment: 'Experimenting',
  practice: 'Practicing',
  strengthen: 'Strengthening',
  integrate: 'Integrating',
}

export const STAGE_COLORS: Record<RoadblockStage, string> = {
  identify: 'bg-brand-subtle text-brand-primary',
  understand: 'bg-brand-subtle text-brand-primary',
  stabilize: 'bg-success/10 text-success',
  experiment: 'bg-warning/10 text-warning',
  practice: 'bg-warning/10 text-warning',
  strengthen: 'bg-success/10 text-success',
  integrate: 'bg-success/10 text-success',
}

export const STAGE_BUTTON_LABELS: Record<RoadblockStage, string> = {
  identify: 'Help me name this',
  understand: 'Help me understand',
  stabilize: 'Ground me',
  experiment: 'Suggest an experiment',
  practice: 'Suggest a practice rep',
  strengthen: 'Show my progress',
  integrate: 'Help me build a habit',
}

// ── Service ────────────────────────────────────────────────────────────────

export class RoadblockService {
  private baseUrl = ROADBLOCKS_V1_ENABLED
    ? '/api/roadblocks/v1/roadblocks'
    : '/api/hierarchy/roadblocks'

  // ── Parent CRUD ──

  async getRoadblocks(): Promise<Roadblock[]> {
    const response = await apiFetch(this.baseUrl, {
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) throw new Error('Failed to fetch roadblocks')
    const data = await response.json()
    if (ROADBLOCKS_V1_ENABLED && data.data) return data.data as Roadblock[]
    return Array.isArray(data) ? data : data.data || []
  }

  async getRoadblock(id: string, detail = false): Promise<Roadblock> {
    const q = detail && ROADBLOCKS_V1_ENABLED ? '?detail=1' : ''
    const response = await apiFetch(`${this.baseUrl}/${id}${q}`, {
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) throw new Error(`Failed to fetch roadblock with id ${id}`)
    const data = await response.json()
    if (ROADBLOCKS_V1_ENABLED && data.data) return data.data as Roadblock
    return data.data || data
  }

  async createRoadblock(roadblock: CreateRoadblockRequest): Promise<Roadblock> {
    const response = await apiFetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(roadblock)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create roadblock' }))
      throw new Error(error.error || 'Failed to create roadblock')
    }
    const data = await response.json()
    if (ROADBLOCKS_V1_ENABLED && data.data) return data.data as Roadblock
    return data.data || data
  }

  async updateRoadblock(id: string, updates: UpdateRoadblockRequest): Promise<Roadblock> {
    const response = await apiFetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    })
    if (!response.ok) throw new Error('Failed to update roadblock')
    const data = await response.json()
    if (ROADBLOCKS_V1_ENABLED && data.data) return data.data as Roadblock
    return data.data || data
  }

  async deleteRoadblock(id: string | number): Promise<void> {
    const response = await apiFetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) throw new Error('Failed to delete roadblock')
  }

  async resolveRoadblock(id: string, resolutionNotes: string): Promise<Roadblock> {
    const response = await apiFetch(`${this.baseUrl}/${id}/resolve`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resolution_notes: resolutionNotes })
    })
    if (!response.ok) throw new Error('Failed to resolve roadblock')
    const data = await response.json()
    if (ROADBLOCKS_V1_ENABLED && data.data) return data.data as Roadblock
    return data.data || data
  }

  async reopenRoadblock(id: string): Promise<Roadblock> {
    const response = await apiFetch(`${this.baseUrl}/${id}/reopen`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) throw new Error('Failed to reopen roadblock')
    const data = await response.json()
    if (ROADBLOCKS_V1_ENABLED && data.data) return data.data as Roadblock
    return data.data || data
  }

  async getRoadblocksForItem(itemId: string, itemType: string): Promise<Roadblock[]> {
    const response = await apiFetch(
      `/api/hierarchy/roadblocks/for-item/${itemType}/${itemId}`,
      { headers: { 'Content-Type': 'application/json' } }
    )
    if (!response.ok) throw new Error('Failed to fetch roadblocks for item')
    const data = await response.json()
    return Array.isArray(data) ? data : data.data || []
  }

  // ── Stage lifecycle ──

  async getStage(roadblockId: string): Promise<StageInfo> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/stage`)
    if (!resp.ok) throw new Error('Failed to fetch stage')
    const data = await resp.json()
    return data.data
  }

  async getStageRecommendation(roadblockId: string): Promise<StageRecommendation> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/stage/recommendation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!resp.ok) throw new Error('Failed to fetch stage recommendation')
    const data = await resp.json()
    return data.data
  }

  async transitionStage(roadblockId: string, toStage: string, reason?: string): Promise<void> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/stage/transition`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to_stage: toStage, reason })
    })
    if (!resp.ok) throw new Error('Failed to transition stage')
  }

  async getStageTransitions(roadblockId: string): Promise<StageTransition[]> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/stage-transitions`)
    if (!resp.ok) throw new Error('Failed to fetch stage transitions')
    const data = await resp.json()
    return data.data || []
  }

  // ── Interventions ──

  async createIntervention(roadblockId: string, payload: Record<string, unknown>): Promise<number> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/interventions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    if (!resp.ok) throw new Error('Failed to create intervention')
    const data = await resp.json()
    return data.id
  }

  async startIntervention(roadblockId: string, interventionId: number, beforeFeeling?: string): Promise<Intervention> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/interventions/${interventionId}/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ before_feeling: beforeFeeling })
    })
    if (!resp.ok) throw new Error('Failed to start intervention')
    const data = await resp.json()
    return data.data
  }

  async completeIntervention(
    roadblockId: string, interventionId: number,
    payload: { effectiveness_score?: number; after_feeling?: string; note_text?: string }
  ): Promise<Intervention> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/interventions/${interventionId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    if (!resp.ok) throw new Error('Failed to complete intervention')
    const data = await resp.json()
    return data.data
  }

  // ── Practice Reps ──

  async createPracticeRep(roadblockId: string, payload: Record<string, unknown>): Promise<number> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/practice-reps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    if (!resp.ok) throw new Error('Failed to create practice rep')
    const data = await resp.json()
    return data.id
  }

  async startPracticeRep(
    roadblockId: string, repId: number,
    payload: { before_fear_score?: number; before_confidence_score?: number }
  ): Promise<PracticeRep> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/practice-reps/${repId}/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    if (!resp.ok) throw new Error('Failed to start practice rep')
    const data = await resp.json()
    return data.data
  }

  async completePracticeRep(
    roadblockId: string, repId: number,
    payload: { after_fear_score?: number; after_confidence_score?: number; outcome_summary?: string; note_text?: string }
  ): Promise<PracticeRep> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/practice-reps/${repId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    if (!resp.ok) throw new Error('Failed to complete practice rep')
    const data = await resp.json()
    return data.data
  }

  // ── Evidence ──

  async createEvidence(roadblockId: string, payload: Record<string, unknown>): Promise<number> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/evidence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    if (!resp.ok) throw new Error('Failed to create evidence')
    const data = await resp.json()
    return data.id
  }

  async pinEvidence(roadblockId: string, evidenceId: number, pin: boolean): Promise<void> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/evidence/${evidenceId}/pin`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_pinned: pin })
    })
    if (!resp.ok) throw new Error('Failed to update evidence pin')
  }

  async unpinEvidence(roadblockId: string, evidenceId: number): Promise<void> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/evidence/${evidenceId}/unpin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!resp.ok) throw new Error('Failed to unpin evidence')
  }

  // ── Suggestions ──

  async acceptSuggestion(roadblockId: string, suggestionId: number): Promise<void> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/suggestions/${suggestionId}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!resp.ok) throw new Error('Failed to accept suggestion')
  }

  async rejectSuggestion(roadblockId: string, suggestionId: number): Promise<void> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/suggestions/${suggestionId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!resp.ok) throw new Error('Failed to reject suggestion')
  }

  async convertSuggestion(roadblockId: string, suggestionId: number): Promise<Record<string, unknown>> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/suggestions/${suggestionId}/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!resp.ok) throw new Error('Failed to convert suggestion')
    const data = await resp.json()
    return data.data
  }

  // ── Progress snapshots ──

  async getProgressSnapshots(roadblockId: string): Promise<Array<Record<string, unknown>>> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/progress-snapshots`)
    if (!resp.ok) throw new Error('Failed to fetch progress snapshots')
    const data = await resp.json()
    return data.data || []
  }

  // ── Follow-ups ──

  async getFollowups(roadblockId: string): Promise<Followup[]> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/followups`)
    if (!resp.ok) throw new Error('Failed to fetch followups')
    const data = await resp.json()
    return (data.data || []).map((f: Record<string, unknown>) => ({
      ...f,
      quick_actions_json: typeof f.quick_actions_json === 'string'
        ? JSON.parse(f.quick_actions_json as string)
        : f.quick_actions_json || []
    }))
  }

  async generateFollowup(roadblockId: string): Promise<Followup | null> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/followups/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!resp.ok) throw new Error('Failed to generate followup')
    const data = await resp.json()
    return data.data || null
  }

  async respondToFollowup(
    roadblockId: string,
    followupId: number,
    body: { response_type: string; raw_text?: string; quick_action_value?: string }
  ): Promise<Record<string, unknown>> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/followups/${followupId}/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    if (!resp.ok) throw new Error('Failed to respond to followup')
    const data = await resp.json()
    return data.data
  }

  async dismissFollowup(roadblockId: string, followupId: number): Promise<void> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/followups/${followupId}/dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!resp.ok) throw new Error('Failed to dismiss followup')
  }

  async snoozeFollowup(roadblockId: string, followupId: number, days = 2): Promise<void> {
    const resp = await apiFetch(`${this.baseUrl}/${roadblockId}/followups/${followupId}/snooze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ snooze_days: days })
    })
    if (!resp.ok) throw new Error('Failed to snooze followup')
  }
}
